<template>
  <div ref="root" class="ref">
    <div class="ref-control">
      <UiInput
        :model-value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        autocomplete="off"
        autocapitalize="off"
        autocorrect="off"
        :spellcheck="false"
        enterkeyhint="done"
        role="combobox"
        :aria-expanded="open"
        aria-autocomplete="list"
        @update:model-value="onType"
        @focus="onFocus"
        @keydown.down.prevent="move(1)"
        @keydown.up.prevent="move(-1)"
        @keydown.enter="onEnter"
        @keydown.esc="close"
      />

      <!--
        Whether this name is linked to a record somewhere else, and the way out.
        Both matter, and nothing else on screen would say which of the two it
        is: a linked name follows the owner when they correct it, a typed one
        stays exactly as it was typed.
      -->
      <button
        v-if="referenceId"
        type="button"
        class="ref-link"
        :title="linkedTitle"
        :aria-label="unlinkLabel"
        :disabled="disabled"
        @click="unlink"
      >
        <span class="ref-link-dot" aria-hidden="true"></span>
      </button>
    </div>

    <!--
      Teleported to the body, and positioned in viewport coordinates.

      A dropdown drawn inside its own container is at the mercy of every
      ancestor's `overflow`, and the card this field usually sits in clips its
      contents to keep its rounded corners — so the list came out cut off. A
      component in the design system cannot depend on where it is used, so it
      leaves the layout entirely and places itself against the box on screen.
    -->
    <Teleport to="body">
      <ul
        v-if="open"
        ref="list"
        class="ref-list"
        role="listbox"
        :style="floating"
      >
        <!-- mousedown.prevent: el campo no pierde el foco. Se elige en el
             click: elegir en el mousedown cerraba la lista y el click del mismo
             toque caía en lo que había debajo (useSuggestionPopover). -->
        <li
          v-for="(item, index) in items" :key="item.id"
          class="ref-item"
          :class="{ 'ref-item--on': index === cursor }"
          role="option" :aria-selected="index === cursor"
          @mousedown.prevent
          @click="pick(item)"
          @mouseenter="cursor = index"
        >
          <!-- El nombre a todo el ancho y, debajo, el código y la pista en
               pequeño: con el código en columna aparte, en un teléfono el
               nombre se partía en cuatro renglones. -->
          <span class="ref-item-label">{{ item.label }}</span>
          <span v-if="item.badge || item.hint" class="ref-item-meta">
            <span v-if="item.badge" class="ref-item-badge">{{ item.badge }}</span>
            <span v-if="item.hint" class="ref-item-hint">{{ item.hint }}</span>
          </span>
        </li>

        <li v-if="!items.length" class="ref-empty">
          {{ searching ? uiLabel('loading') : uiLabel('noOptions') }}
        </li>
      </ul>
    </Teleport>
  </div>
</template>

<script setup>
import { onUnmounted, ref, watch } from 'vue'

import UiInput from './UiInput.vue'
import { api } from '../api/client'
import { uiLabel } from '../utils/labels'
import { isTouchScreen, useSuggestionPopover } from '../composables/useSuggestionPopover'

/**
 * A text box that happens to suggest what another module owns.
 *
 * Two things are true at once and this field has to hold both. Something with a
 * record elsewhere should be POINTED AT rather than retyped — that is what lets
 * the name follow when its owner corrects it. And the form must stay usable for
 * something with no record at all: a supplier the company bought from once, an
 * instance that bought this module and nothing else.
 *
 * So: typing always works. If nothing on this instance answers for the type —
 * the owning module is not installed, or this user may not read it — no
 * suggestion ever appears and what is left is exactly the plain input it would
 * otherwise have been. The platform answers `available: false` with a 200 for
 * precisely that reason, so there is nothing to catch and nothing to hide.
 *
 * It lives here rather than in a module because it is the second one: `quotes`
 * already has one of these for customers, and a third module writing its own
 * would be the copy §4.1 exists to stop. It never talks to the module that
 * owns the data — it asks the PLATFORM for a type, and never learns who
 * answered (§6.6).
 */

const props = defineProps({
  /** The reference type to look up: 'supplier', 'customer', 'material'. */
  type: { type: String, required: true },

  /**
   * El padre dentro del que se busca: «una partida de esta obra».
   *
   * Sin él, una referencia hija no se puede usar: enseñar las partidas de todas
   * las obras no le sirve a nadie. Los dos juntos o ninguno.
   */
  scopeType: { type: String, default: '' },
  scopeId: { type: String, default: '' },
  /** The name. Free text, always editable, and what gets stored either way. */
  modelValue: { type: String, default: '' },
  /** The id of the record this name points at, or null when it was typed. */
  referenceId: { type: String, default: null },
  placeholder: { type: String, default: '' },
  /** Shown on the link marker. The module supplies it: it knows its own words. */
  linkedTitle: { type: String, default: '' },
  unlinkLabel: { type: String, default: '' },
  disabled: Boolean,
})

/**
 * `picked` lleva la fila ENTERA que se eligió: id, etiqueta, distintivo y el
 * `meta` que puso quien la provee.
 *
 * El id solo no basta en cuanto la referencia trae datos que la pantalla quiere
 * copiar —la unidad de una partida, el disponible de un paquete— y la
 * alternativa es que cada pantalla vuelva a pedir por id lo que acaba de
 * recibir. Se emite además de los otros dos, así que quien no lo escuche sigue
 * igual.
 */
const emit = defineEmits(['update:modelValue', 'update:referenceId', 'picked'])

/** El alcance en la URL, o nada. Los dos o ninguno: medio alcance no acota. */
function scopeQuery(join) {
  if (!props.scopeType || !props.scopeId) return ''
  return `${join}scopeType=${encodeURIComponent(props.scopeType)}`
    + `&scopeId=${encodeURIComponent(props.scopeId)}`
}

const root = ref(null)
const list = ref(null)
const items = ref([])

const cursor = ref(-1)
const open = ref(false)
const searching = ref(false)

// Dónde se dibuja la lista teleportada, qué la cierra y cuándo sube el campo
// con el teclado fuera: lo mismo para todos los autocompletados.
const { style: floating, reveal } = useSuggestionPopover({
  anchor: root, list, open, onDismiss: () => close(), maxHeight: 260,
})

// Asked once, on the first focus, and remembered. `null` is "not asked yet";
// after that it is a boolean and no further probe is made — whether a module is
// installed does not change while somebody fills in a form.
const available = ref(null)

let timer = null
let sequence = 0

async function probe() {
  if (available.value !== null) return
  try {
    // An empty term is how a field asks "is there anybody there?". The platform
    // answers it without running a search, so the probe is free.
    const answer = await api.get(`/references/${props.type}${scopeQuery('?')}`)
    available.value = answer.available === true
  } catch {
    // A lookup that cannot be made is a field that does not suggest. It is
    // never a reason to stop somebody typing.
    available.value = false
  }
}

/**
 * Focusing opens the list.
 *
 * With something typed it searches for it. With the box EMPTY it browses: the
 * first few of whatever this type is, so somebody who has just chosen
 * «Emplazamiento» can see what the places are called instead of having to guess
 * a name first. A field that stays blank until you type only works for whoever
 * already knew the answer, which is never the person who needed the picker.
 *
 * It costs one request per focus on an empty field, and the platform is the one
 * that decides how many rows that is.
 */
function onFocus() {
  reveal()
  probe().then(() => {
    if (!available.value) return
    const typed = props.modelValue?.trim()
    if (typed) search(typed)
    else browse()
  })
}

async function browse() {
  const mine = ++sequence
  searching.value = true
  open.value = true

  try {
    const answer = await api.get(`/references/${props.type}?browse=true&limit=8${scopeQuery('&')}`)
    if (mine !== sequence) return
    items.value = answer.items ?? []
    cursor.value = -1
  } catch {
    // Same rule as searching: a lookup that cannot be made is a field that does
    // not suggest, never a field that stops somebody typing.
    if (mine === sequence) { items.value = []; open.value = false }
  } finally {
    if (mine === sequence) searching.value = false
  }
}

function onType(value) {
  emit('update:modelValue', value)

  // Typing over a link breaks it. The alternative — keeping the id while the
  // name says something else — is a row that claims to point at a record it
  // does not name.
  if (props.referenceId) emit('update:referenceId', null)

  search(value)
}

function search(term) {
  clearTimeout(timer)

  const wanted = String(term ?? '').trim()
  // Clearing the box goes back to the list rather than closing it. Deleting what
  // you typed is «none of those, show me again», not «I am done here».
  if (!wanted) return browse()

  // Waits for the typing to stop. Every keystroke is not a question worth
  // asking, and the platform rate-limits this endpoint on the assumption that
  // somebody debounces.
  timer = setTimeout(async () => {
    await probe()
    if (!available.value) return close()

    const mine = ++sequence
    searching.value = true
    open.value = true

    try {
      const answer = await api.get(`/references/${props.type}?search=${encodeURIComponent(wanted)}&limit=8${scopeQuery('&')}`)
      // A slower earlier request must not overwrite a faster later one, or the
      // list ends up showing the results for a prefix of what was typed.
      if (mine !== sequence) return
      items.value = answer.items ?? []
      cursor.value = -1
    } catch {
      if (mine === sequence) items.value = []
    } finally {
      if (mine === sequence) searching.value = false
    }
  }, 250)
}

function pick(item) {
  emit('update:modelValue', item.label)
  emit('update:referenceId', item.id)
  emit('picked', item)
  close()
  // Con el dedo, elegir es haber terminado con este campo: se guarda el
  // teclado, como hace un formulario nativo. Con el ratón el foco se queda.
  if (isTouchScreen()) root.value?.querySelector('input')?.blur()
}

function unlink() {
  // The name stays. Only the link goes: somebody detaching a row usually means
  // "this one is not that record", not "forget what it is called".
  emit('update:referenceId', null)
  emit('picked', null)
}

function move(step) {
  if (!open.value || !items.value.length) return
  cursor.value = (cursor.value + step + items.value.length) % items.value.length
}

function onEnter(event) {
  if (!open.value || cursor.value < 0) return
  event.preventDefault()
  pick(items.value[cursor.value])
}

function close() {
  open.value = false
  cursor.value = -1
}

/**
 * The type can change under the field.
 *
 * A screen that asks «what is this order about?» — a customer, a machine, a
 * location — is one field whose `type` changes with a dropdown beside it.
 * `available` is probed once and cached, so without this the field would keep
 * answering for the type it was first focused on: suggesting nothing where the
 * new type has a provider, or suggesting against a type nobody answers for.
 */
watch(() => props.type, () => {
  available.value = null
  items.value = []
  close()
})

onUnmounted(() => clearTimeout(timer))
</script>

<style scoped>
.ref { position: relative; }

.ref-control { position: relative; }

/* The link marker sits inside the box, on the right, where a clear button
   would. It is a button because it does something: it detaches the link. */
.ref-link {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: none;
  cursor: pointer;
}
.ref-link:disabled { cursor: default; opacity: .5; }
/* With a finger, 20 px is a guess: unlinking a reference took several tries
   (found on a phone in the package panel of a works file). The dot stays the
   same size; only the area that takes the tap grows, and the text makes room
   for it. */
@media (pointer: coarse) {
  .ref-link { width: 36px; height: 36px; right: 2px; }
  .ref-control:has(.ref-link) :deep(input) { padding-right: 40px; }
}

.ref-link-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-success, #16a34a);
}

/* Fixed, because it is teleported to the body and placed in viewport
   coordinates — `top`, `left` and `width` come from the inline style the
   component computes. Above the modal (185) and below the toasts (200): a
   picker inside a dialog has to open over it, and a toast has to survive both.
   Scoped styles still reach it: Vue keeps the scope id on teleported nodes. */
.ref-list {
  position: fixed;
  z-index: 190;
  margin: 0;
  padding: 4px;
  list-style: none;
  max-height: 260px;
  overflow-y: auto;
  /* Llegar al final de la lista con el dedo no arrastra la página de detrás,
     que movía el campo y con él la lista. */
  overscroll-behavior: contain;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg, 0 10px 30px rgba(0, 0, 0, .12));
}

/* Los tokens son los del tema (theme/*.css): --text-xs y --color-text-mute.
   Con --font-size-xs y --color-text-muted, que no existen, el código y la
   pista salían al tamaño y en el color del nombre. */
.ref-item {
  padding: 8px 10px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: var(--text-sm);
  line-height: 1.35;
}
.ref-item + .ref-item { margin-top: 2px; }
.ref-item--on { background: var(--color-surface-2); }

.ref-item-label {
  font-weight: 600;
  color: var(--color-text);
  /* Dos renglones como mucho: lo que sobra lo dice el title del navegador
     al pasar y, elegido, el propio campo. */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.ref-item-meta {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
  font-size: var(--text-xs);
  color: var(--color-text-mute);
}
.ref-item-badge {
  font-family: var(--font-mono, ui-monospace, monospace);
  flex-shrink: 0;
}
.ref-item-hint {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ref-empty {
  padding: 10px;
  text-align: center;
  font-size: var(--text-sm);
  color: var(--color-text-mute);
}

/* Con el dedo, una fila de 44 px como mínimo: a 30 se elegía la de al lado. */
@media (pointer: coarse) {
  .ref-item { min-height: 52px; justify-content: center; padding: 10px 12px; }
  .ref-item { font-size: var(--text-md); }
}
</style>
